import { z } from "zod";
import { LanguageCodeSchema, TranslationMapSchema } from "./utils";
import { SurveyImportVisibleIfSchema } from "./surveyVisibility";

// Defined in ./utils, which is where every other shared primitive lives.
// Re-exported here because most of the package's translated text hangs off
// surveys and a good deal of code imports it from this module.
export { TranslationMapSchema };

export const OptionResponseSchema = z.object({
  id: z.string(),
  text: TranslationMapSchema,
  /**
   * The instrument's own response code, which is what skip logic compares
   * against — `visibleIf` names an option by its VALUE, never by the cuid the
   * database gave it, so a client cannot evaluate a condition without this.
   * Null on a question authored before the importer existed.
   */
  value: z.number().int().nullable(),
});
export type OptionResponse = z.infer<typeof OptionResponseSchema>;

/**
 * The two fields every question carries so a client can resolve skip logic.
 *
 * They are on the wire together because neither is usable alone: a condition
 * names a question by `key` and an answer by `Option.value`, so exposing the
 * condition without the key it references would be a rule pointing at nothing.
 * Evaluation itself is `resolveVisibility` in `surveyVisibility.ts`, shared with
 * the API so the two cannot disagree about what the patient was asked.
 */
const QuestionVisibilityFields = {
  /** The instrument's variable id (`KPREM_Q04`). Null on a legacy question. */
  key: z.string().nullable(),
  /** Null means the question is always shown. */
  visibleIf: SurveyImportVisibleIfSchema.nullable(),
};

export const DomainResponseSchema = z.object({
  id: z.string(),
  text: TranslationMapSchema,
});
export type DomainResponse = z.infer<typeof DomainResponseSchema>;

export const questionTypeSchema = z.enum(["SINGLE", "MULTIPLE", "RANGE"]);

export const QuestionSingleChoiceResponseSchema = z.object({
  id: z.string(),
  text: TranslationMapSchema,
  type: z.literal("SINGLE"),
  help: TranslationMapSchema.nullable(),
  options: z.array(OptionResponseSchema),
  domain: DomainResponseSchema,
  ...QuestionVisibilityFields,
});
export const QuestionMultipleChoicesResponseSchema = z.object({
  id: z.string(),
  text: TranslationMapSchema,
  type: z.literal("MULTIPLE"),
  help: TranslationMapSchema.nullable(),
  options: z.array(OptionResponseSchema),
  domain: DomainResponseSchema,
  ...QuestionVisibilityFields,
});
/** A label for one point on a scale. Display only, never an answer. */
export const AnchorResponseSchema = z.object({
  value: z.number().int(),
  text: TranslationMapSchema,
});
export type AnchorResponse = z.infer<typeof AnchorResponseSchema>;

export const QuestionRangeResponseSchema = z.object({
  id: z.string(),
  text: TranslationMapSchema,
  help: TranslationMapSchema.nullable(),
  type: z.literal("RANGE"),
  minValue: z.number().int(),
  maxValue: z.number().int(),
  /**
   * Words for points on the track, usually its two ends. Empty on a scale
   * whose numbers speak for themselves; without them a 1-to-7 question is
   * seven bare numbers with no way to tell which end is the good one.
   */
  anchors: z.array(AnchorResponseSchema),
  /**
   * Answers that are not points on the scale ("Don't know", "Not applicable"),
   * rendered as buttons beside the track. Real options with real response
   * codes, which is why an answer to a RANGE question is either a magnitude or
   * one of these, never both.
   */
  secondaryOptions: z.array(OptionResponseSchema),
  domain: DomainResponseSchema,
  ...QuestionVisibilityFields,
});
export const QuestionResponseSchema = z.discriminatedUnion("type", [
  QuestionSingleChoiceResponseSchema,
  QuestionMultipleChoicesResponseSchema,
  QuestionRangeResponseSchema,
]);
export type QuestionResponse = z.infer<typeof QuestionResponseSchema>;

export const CreatorInfoSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});
export type CreatorInfo = z.infer<typeof CreatorInfoSchema>;

export const SurveyResponseSchema = z.object({
  id: z.string(),
  title: TranslationMapSchema,
  description: TranslationMapSchema,
  // coerce: clients parse ISO strings, the server validates Date objects.
  createdAt: z.coerce.date(),
  questions: z.array(QuestionResponseSchema),
  domains: z.array(
    z.object({ id: z.string(), key: z.string(), text: TranslationMapSchema }),
  ),
  createdBy: CreatorInfoSchema,
  languages: z.array(LanguageCodeSchema),
  /**
   * Which scoring plugin reads this questionnaire, or null for one that is
   * collected but not scored.
   *
   * This is what splits the catalog into two kinds of survey. A scored one
   * arrives as part of a plugin PACKAGE (questionnaire and scoring versioned
   * together, installed from `POST /analysis/plugins/:id/install`) and is
   * effectively read-only; an unscored one is a questionnaire someone uploaded.
   */
  analysisPluginId: z.string().nullable(),
  /** The package version a scored questionnaire was installed from. */
  analysisPluginVersion: z.string().nullable(),
});
export type SurveyResponse = z.infer<typeof SurveyResponseSchema>;

const DomainByIdSchema = z.object({ id: z.string() });
const DomainByKeySchema = z.object({ key: z.string().min(1) });
export const DomainCreateOrConnectSchema = z.union([
  DomainByIdSchema,
  DomainByKeySchema,
]);
export type DomainCreateOrConnect = z.infer<typeof DomainCreateOrConnectSchema>;

export const QuestionBaseCreateSchema = z.object({
  text: TranslationMapSchema,
  type: questionTypeSchema,
  help: TranslationMapSchema.optional(),
  domain: DomainCreateOrConnectSchema,
});
export const QuestionSingleChoiceCreateSchema = QuestionBaseCreateSchema.extend(
  {
    type: z.literal("SINGLE"),
    options: z.array(TranslationMapSchema).min(1),
  },
);
export const QuestionMultipleChoicesCreateSchema =
  QuestionBaseCreateSchema.extend({
    type: z.literal("MULTIPLE"),
    options: z.array(TranslationMapSchema).min(1),
  });
export const QuestionRangeCreateSchema = QuestionBaseCreateSchema.extend({
  type: z.literal("RANGE"),
  minValue: z.number().int(),
  maxValue: z.number().int(),
});
// The min<=max check lives on the union, not on the RANGE member: a refined
// member is no longer a plain object schema, and z.discriminatedUnion needs
// one to read the discriminator. It used to be declared on the member and
// silently dropped, so the API accepted an unanswerable min > max range.
export const QuestionCreateBodySchema = z
  .discriminatedUnion("type", [
    QuestionSingleChoiceCreateSchema,
    QuestionMultipleChoicesCreateSchema,
    QuestionRangeCreateSchema,
  ])
  .superRefine((val, ctx) => {
    if (val.type === "RANGE" && val.minValue > val.maxValue) {
      ctx.addIssue({
        code: "custom",
        message: "minValue must be <= maxValue",
        path: ["minValue"],
      });
    }
  });
export type QuestionCreateBody = z.infer<typeof QuestionCreateBodySchema>;

const QuestionSingleChoiceUpdateSchema =
  QuestionSingleChoiceCreateSchema.partial();
const QuestionMultipleChoicesUpdateSchema =
  QuestionMultipleChoicesCreateSchema.partial();
const QuestionRangeUpdateSchema = QuestionRangeCreateSchema.partial();
export const QuestionUpdateBodySchema = z.union([
  QuestionSingleChoiceUpdateSchema,
  QuestionMultipleChoicesUpdateSchema,
  QuestionRangeUpdateSchema,
]);
export type QuestionUpdateBody = z.infer<typeof QuestionUpdateBodySchema>;

export type SurveyUpdateBody = {
  title?: Record<string, string>;
  description?: Record<string, string>;
};

export const listSurveysSchemas = {
  querystring: z.object({
    // 0-based, like studies and domains. It was .positive(), which rejected
    // page=0 while the handler did skip = page * pageSize — so ?page=1
    // silently skipped the first page.
    page: z.coerce.number().int().nonnegative().optional(),
    pageSize: z.coerce.number().int().positive().optional(),
  }),
  response: {
    200: z.object({
      surveys: z.array(SurveyResponseSchema),
      totalPages: z.number(),
      currentPage: z.number(),
    }),
  },
};
export type ListSurveysQuery = z.infer<typeof listSurveysSchemas.querystring>;

export const getSurveySchemas = {
  params: z.object({ id: z.string().cuid() }),
  response: { 200: SurveyResponseSchema },
};
export type GetSurveyParams = z.infer<typeof getSurveySchemas.params>;

export const createSurveySchemas = {
  body: z.object({
    title: TranslationMapSchema,
    description: TranslationMapSchema,
    domains: z
      .array(z.object({ key: z.string().min(1), text: TranslationMapSchema }))
      .optional()
      .default([]),
    questions: z.array(QuestionCreateBodySchema).optional().default([]),
  }),
  response: { 201: SurveyResponseSchema },
};
export type CreateSurveyInput = z.infer<typeof createSurveySchemas.body>;

export const updateSurveySchemas = {
  params: z.object({ id: z.string().cuid() }),
  body: z.object({
    title: TranslationMapSchema.optional(),
    description: TranslationMapSchema.optional(),
  }),
  response: { 200: SurveyResponseSchema },
};
export type UpdateSurveyParams = z.infer<typeof updateSurveySchemas.params>;
export type UpdateSurveyInput = z.infer<typeof updateSurveySchemas.body>;

export const createQuestionSchemas = {
  params: z.object({ id: z.string().cuid() }),
  body: QuestionCreateBodySchema,
  response: { 201: QuestionResponseSchema },
};
export type CreateQuestionParams = z.infer<typeof createQuestionSchemas.params>;

export const updateQuestionSchemas = {
  params: z.object({ id: z.string().cuid(), qid: z.string().cuid() }),
  body: QuestionUpdateBodySchema,
  response: { 200: QuestionResponseSchema },
};
export type UpdateQuestionParams = z.infer<typeof updateQuestionSchemas.params>;

export const deleteQuestionSchemas = {
  params: z.object({ id: z.string().cuid(), qid: z.string().cuid() }),
  response: { 204: z.undefined() },
};
export type DeleteQuestionParams = z.infer<typeof deleteQuestionSchemas.params>;
